import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { GitCompare, Trophy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { EmptyState, SkeletonRows } from "@/components/app/Common";
import { MiniBar, RecChip, ConfLabel } from "@/components/fit/FitBits";
import { get } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { STAFF } from "@/lib/nav";
import { stageLabel } from "@/lib/charts";
import { COMP_COLORS, COMP_LABELS, DECISION_LABEL, fmtFit } from "@/lib/fit";
import { WeightsEditor } from "@/pages/job/ranking/WeightsEditor";
import { ThresholdsCard, ManagersCard } from "@/pages/job/ranking/ThresholdsCard";
import { cn } from "@/lib/utils";
import { ProvisionalTag, LeaningChip, waitingOn } from "@/components/fit/Provisional";

export function FinalRanking({ job }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const canEdit = STAFF.includes(user.role);
  const [sel, setSel] = useState([]);
  const [limit, setLimit] = useState(50);
  const { data: lb, isLoading } = useQuery({ queryKey: ["leaderboard", job.id], queryFn: () => get(`/jobs/${job.id}/leaderboard`), refetchInterval: 8000 });
  if (isLoading) return <SkeletonRows rows={8} />;
  const toggle = (id) => setSel(sel.includes(id) ? sel.filter((x) => x !== id) : sel.length < 4 ? [...sel, id] : sel);
  return (
    <div className="grid gap-6 xl:grid-cols-[1fr_340px]" data-tour="pt-final-ranking" data-testid="final-ranking">
      <section className="panel overflow-hidden">
        <div className="flex flex-wrap items-center gap-3 border-b border-line px-5 py-3">
          <h3 className="text-base font-semibold">Leaderboard by Fit Score</h3>
          <div className="flex flex-wrap gap-3 text-xs text-ink-muted">{Object.keys(COMP_COLORS).filter((k) => lb.weights[k] > 0).map((k) => <span key={k} className="flex items-center gap-1"><span className="h-2 w-2 rounded-full" style={{ background: COMP_COLORS[k] }} />{COMP_LABELS[k]}</span>)}</div>
          <Button size="sm" className="ml-auto" disabled={sel.length < 2} onClick={() => nav(`/app/compare?ids=${sel.join(",")}`)} data-tour="pt-compare-selected-btn" data-testid="compare-selected-btn"><GitCompare size={14} /> Compare selected ({sel.length})</Button>
        </div>
        {!lb.rows.length ? <EmptyState icon={Trophy} title="No scored candidates yet" text="Fit Scores appear once resumes are screened." /> : (
          <div className="divide-y divide-line">
            {lb.rows.slice(0, limit).map((r, i) => (
              <div key={r.application_id} tabIndex={0} onKeyDown={(e) => e.key === "Enter" && nav(`/app/candidates/${r.candidate_id}/overview`)}
                className={cn("tl-row grid grid-cols-[28px_36px_1fr_56px] items-center gap-3 px-5 py-3 text-sm md:grid-cols-[28px_28px_1.4fr_56px_1fr_176px_96px]", sel.includes(r.application_id) && "bg-brand-tint/50")}
                onClick={() => nav(`/app/candidates/${r.candidate_id}/overview`)} data-testid={`leaderboard-row-${r.application_id}`}>
                <span onClick={(e) => e.stopPropagation()}><Checkbox checked={sel.includes(r.application_id)} onCheckedChange={() => toggle(r.application_id)} data-testid={`compare-select-${r.application_id}`} /></span>
                <span className="font-display text-base text-ink-muted tabular-nums">{i + 1}</span>
                <span className="min-w-0"><span className="block truncate font-medium">{r.name}</span><span className="text-xs text-ink-muted">{stageLabel(r.stage)}{r.decision ? ` · ${DECISION_LABEL[r.decision]}` : ""}{r.overridden ? " · overridden" : ""}{r.provisional && <ProvisionalTag testId={`leaderboard-provisional-${r.application_id}`} />}</span></span>
                <span className="font-display text-xl font-semibold tabular-nums text-ink" data-testid={`leaderboard-fit-${r.application_id}`}>{fmtFit(r.fit_score)}</span>
                <span className="hidden md:block"><MiniBar components={r.components} testId={`leaderboard-bar-${r.application_id}`} /></span>
                <span className="hidden md:block" title={r.provisional ? `Waiting on: ${waitingOn(r.missing)}` : undefined}>{r.provisional ? <LeaningChip rec={r.recommendation} /> : <RecChip rec={r.recommendation} />}</span>
                <span className="hidden md:block"><ConfLabel c={r.confidence} /></span>
              </div>
            ))}
            {lb.rows.length > limit && <button onClick={() => setLimit(limit + 100)} className="w-full px-5 py-3 text-sm font-medium text-brand hover:bg-subtle" data-testid="leaderboard-show-more">Show {Math.min(100, lb.rows.length - limit)} more of {lb.rows.length - limit}</button>}
          </div>
        )}
      </section>
      <div className="space-y-5">
        <WeightsEditor job={job} lb={lb} canEdit={canEdit} />
        <ThresholdsCard job={job} lb={lb} canEdit={canEdit} />
        {canEdit && <ManagersCard job={job} />}
      </div>
    </div>
  );
}
